import { ref, type Ref, type MaybeRefOrGetter, computed, toValue, type ComputedRef, watch } from 'vue';
import { areValuesEqual, getValue } from '../../../shared/utils';
import type { TrackBy } from '../../../shared/types';

type Key = string | number;

interface UseItemsSelectionOptions<T> {
  trackBy?: TrackBy<T, Key>;
  itemSelectionDisabled?: TrackBy<T, boolean>;
  totalItems?: MaybeRefOrGetter<number>;
  selectedItems?: Ref<T[]>;
  allItemsSelected?: Ref<boolean>;
  reference?: MaybeRefOrGetter<T[]>;
}

interface UseItemsSelectionReturn<T> {
  selectedItems: Ref<T[]>;
  allItemsSelected: Ref<boolean>;
  selectedItemsCount: ComputedRef<number>;
  clearSelection: () => void;
  selectItem: (item: T) => void;
  unselectItem: (item: T) => void;
  isItemSelected: (item: T) => boolean;
  selectRange: (items: readonly T[], from: number, to: number, selected?: boolean) => void;
}

export function useItemsSelection<T>(options?: UseItemsSelectionOptions<T>): UseItemsSelectionReturn<T> {
  const selectedItems = options?.selectedItems ?? (ref<T[]>([]) as Ref<T[]>);
  const allItemsSelected = options?.allItemsSelected ?? ref(false);

  function getItemKey(item: T) {
    if (options?.trackBy) {
      return getValue(item, options.trackBy);
    }

    return item;
  }

  const selectedKeys = computed(() => new Set(selectedItems.value.map(getItemKey)));

  function areItemsEqual(item1: T, item2: T) {
    if (options?.trackBy) {
      return areValuesEqual(item1, item2, options.trackBy);
    }

    return item1 === item2;
  }

  function isItemSelected(item: T): boolean {
    return selectedKeys.value.has(getItemKey(item));
  }

  function isItemSelectionDisabled(item: T): boolean {
    if (!options?.itemSelectionDisabled) {
      return false;
    }

    return !!getValue(item, options.itemSelectionDisabled);
  }

  function clearSelection(): void {
    selectedItems.value = [];
    allItemsSelected.value = false;
  }

  function selectItem(item: T): void {
    if (isItemSelected(item)) {
      return;
    }

    selectedItems.value = [...selectedItems.value, item];
  }

  function unselectItem(item: T): void {
    selectedItems.value = selectedItems.value.filter((selectedItem) => {
      if (options?.trackBy) {
        return !areItemsEqual(selectedItem, item);
      }

      return selectedItem !== item;
    });
  }

  function selectRange(items: readonly T[], from: number, to: number, selected = true): void {
    if (allItemsSelected.value) {
      allItemsSelected.value = false;
    }

    const [a, b] = from <= to ? [from, to] : [to, from];

    if (selected) {
      const itemsToAdd: T[] = [];
      for (let i = a; i <= b; i++) {
        const item = items[i];
        if (!item) continue;
        if (!isItemSelected(item) && !isItemSelectionDisabled(item)) {
          itemsToAdd.push(item);
        }
      }
      if (itemsToAdd.length > 0) {
        selectedItems.value = [...selectedItems.value, ...itemsToAdd];
      }
    } else {
      const itemsToRemove: T[] = [];
      for (let i = a; i <= b; i++) {
        const item = items[i];
        if (!item) continue;
        itemsToRemove.push(item);
      }
      if (itemsToRemove.length > 0) {
        selectedItems.value = selectedItems.value.filter((selectedItem) => {
          return !itemsToRemove.some((item) => areItemsEqual(selectedItem, item));
        });
      }
    }
  }

  const selectedItemsCount = computed(() => {
    if (allItemsSelected.value) {
      return toValue(options?.totalItems) ?? 0;
    }

    return selectedItems.value.length;
  });

  const reference = computed(() => toValue(options?.reference));

  watch(
    reference,
    () => {
      if (!reference.value || !selectedItems.value.length) {
        return;
      }

      if (!reference.value.length) {
        clearSelection();
        return;
      }

      const referenceKeys = new Set(reference.value.map(getItemKey));
      selectedItems.value = selectedItems.value.filter((item) => referenceKeys.has(getItemKey(item)));
    },
    {
      immediate: true,
    },
  );

  return {
    selectedItems,
    allItemsSelected,
    clearSelection,
    selectItem,
    unselectItem,
    isItemSelected,
    selectedItemsCount,
    selectRange,
  };
}
